import { ReactNode } from "react";

import {
  CO_SIGNING_VIEW,
  ENROLLMENT_STATUS,
  MULTISIG_ORG_STATUS,
  MULTISIG_SIGNER_ROLE,
  MULTISIG_SIGNER_STATUS,
} from "@/constants/multisigEnrollment";
import type { CoSigningView } from "@/constants/multisigEnrollment";

import { isActiveEnrollmentStatus } from "@/apiQueries/multisigEnrollment";

import type { CoSigningStatus, MultisigActiveSigner, UserRole } from "@/types";

export type { CoSigningView };

/**
 * Roles that can be enrolled as multisig signers. Following the
 * `multisig_signer_role` enum on the backend.
 */
const SIGNER_ELIGIBLE_ROLES: ReadonlyArray<UserRole> = ["owner"];

export const isMultisigEligible = (role: UserRole | null | undefined): boolean =>
  !!role && SIGNER_ELIGIBLE_ROLES.includes(role);

export const deriveCoSigningView = (status: CoSigningStatus | undefined): CoSigningView => {
  if (!status) return CO_SIGNING_VIEW.INITIALIZE;
  if (!isMultisigEligible(status.me.role)) return CO_SIGNING_VIEW.NOT_APPLICABLE;

  // The org stays BACKEND_ONLY until the session completes, so an in-flight
  // session decides the view: Owner 2 acts while it waits for their key,
  // Owner 1 resumes the wizard while it waits for theirs, and everyone else
  // waits.
  const session = status.enrollment;

  // A lapsed setup gets its own view, so the card explains what happened
  // instead of silently offering a fresh setup.
  //
  // Today this only fires if a caller holds a session the query never serves:
  // `GET /multisig/enrollment` runs ExpireStale and then returns only the four
  // active statuses, so an expired one comes back as a 404 (null) and the card
  // falls through to INITIALIZE with no explanation. Telling the Owner their
  // 4-day setup lapsed needs the backend to expose the lapsed enrollment; the
  // branch is here so the view logic stays complete over the status union.
  if (session?.status === ENROLLMENT_STATUS.EXPIRED) return CO_SIGNING_VIEW.EXPIRED;

  if (session && isActiveEnrollmentStatus(session.status)) {
    const userId = status.me.user_id;
    if (
      session.status === ENROLLMENT_STATUS.PENDING_OWNER2_KEY &&
      userId === session.owner2_user_id
    ) {
      return CO_SIGNING_VIEW.MY_TURN;
    }
    if (
      session.status === ENROLLMENT_STATUS.PENDING_OWNER1_KEY &&
      userId === session.initiator_user_id
    ) {
      return CO_SIGNING_VIEW.INITIALIZE;
    }
    return CO_SIGNING_VIEW.PENDING_OTHERS;
  }

  if (status.org.multisig_status === MULTISIG_ORG_STATUS.BACKEND_ONLY)
    return CO_SIGNING_VIEW.INITIALIZE;

  if (status.me.signer_status !== MULTISIG_SIGNER_STATUS.ACTIVE) return CO_SIGNING_VIEW.MY_TURN;

  if (status.org.active_signers.length < status.org.required_signers) {
    return CO_SIGNING_VIEW.PENDING_OTHERS;
  }

  return CO_SIGNING_VIEW.ACTIVE;
};

export type CoSigningActionVariant = "primary" | "secondary" | "tertiary";

export type CoSigningCopy = {
  label: string | ReactNode;
  note: ReactNode;
  actionLabel?: string;
  actionVariant?: CoSigningActionVariant;
};

const fullName = (person: { first_name: string; last_name: string } | undefined): string => {
  if (!person) return "your co-signer";
  const name = `${person.first_name} ${person.last_name}`.trim();
  return name || "your co-signer";
};

const nextPendingSignerName = (status: CoSigningStatus | undefined): string =>
  fullName(status?.org.pending_signer);

const orgNameOr = (fallback: string) => (name?: string) => name?.trim() || fallback;
const orgName = orgNameOr("your organization");

export const buildCoSigningCopy = (
  view: CoSigningView,
  status: CoSigningStatus | undefined,
  organizationName: string | undefined,
): CoSigningCopy => {
  const org = orgName(organizationName);
  const nextSigner = nextPendingSignerName(status);
  const role = status?.me.role;
  const footer = (
    <span className="Note__emphasis">You&apos;ll need two Owners to get started.</span>
  );

  switch (view) {
    case CO_SIGNING_VIEW.INITIALIZE:
      return {
        label: "Require two co-signers for every disbursement",
        note: (
          <>
            <span>
              Once set up, no one at your organization can send funds alone. Every disbursement must
              be signed by two team members with VIA Wallet before it&apos;s submitted.
            </span>
            {footer}
          </>
        ),
        actionLabel: "Set up",
        actionVariant: "tertiary",
      };

    case CO_SIGNING_VIEW.MY_TURN:
      return {
        label: (
          <>
            <span>
              <strong>{status?.me?.invited_by ? fullName(status.me.invited_by) : "Owner 1"}</strong>{" "}
              invited you to be a co-signer
            </span>
          </>
        ),

        note: (
          <>
            <span>
              You&apos;ve been invited to help protect {org}&apos;s disbursements. Once co-signing
              is set up, no payment can go out on one person&apos;s approval — each one needs two
              co-signers to sign off. To take part, set up your signing key in VIA Wallet.
            </span>
          </>
        ),
        actionLabel:
          role === "financial_controller"
            ? "Set up co-signing key"
            : "Accept and set up co-signing",
        actionVariant: "primary",
      };

    case CO_SIGNING_VIEW.PENDING_OTHERS:
      return {
        label: (
          <>
            <span>
              Co-signing is almost active
              <span
                style={{
                  backgroundColor: "var(--sds-clr-amber-03)",
                  color: "var(--sds-clr-amber-11)",
                  padding: "5px",
                  marginLeft: "10px",
                  borderRadius: "4px",
                  textTransform: "uppercase",
                }}
              >
                Pending {nextSigner}'s setup
              </span>
            </span>
          </>
        ),

        note: (
          <>
            <span>
              Your co-signing key is set up. Once {nextSigner} sets up theirs, every disbursement at{" "}
              {org} will need two co-signers before funds go out.
            </span>
          </>
        ),
        actionLabel: "Edit co-signer setup",
        actionVariant: "tertiary",
      };
    case CO_SIGNING_VIEW.EXPIRED:
      return {
        label: (
          <>
            <span>
              Co-signing setup expired
              <span
                style={{
                  backgroundColor: "var(--sds-clr-red-03)",
                  color: "var(--sds-clr-red-11)",
                  padding: "5px",
                  marginLeft: "10px",
                  borderRadius: "4px",
                  textTransform: "uppercase",
                }}
              >
                Not set up
              </span>
            </span>
          </>
        ),

        note: (
          <>
            <span>
              Your co-signing key is set up. Once {nextSigner} sets up theirs, every disbursement at{" "}
              {org} will need two co-signers before funds go out.
            </span>
          </>
        ),
        actionLabel: "Edit co-signer setup",
        actionVariant: "tertiary",
      };

    case CO_SIGNING_VIEW.ACTIVE:
      return {
        label: (
          <div>
            Co-signing is{" "}
            <span
              style={{
                backgroundColor: " #e9f9ee",
                color: "#18794e",
                borderRadius: "10px",
                padding: "4px 10px",
              }}
            >
              <svg
                width="11"
                height="11"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#18794e"
                strokeWidth="1.75"
                strokeLinecap="round"
                strokeLinejoin="round"
                name="check"
              >
                <path d="M20 6 9 17l-5-5"></path>
              </svg>
              ON
            </span>
          </div>
        ),
        note: (
          <>
            <span>
              Every disbursement at {org} needs two co-signers in VIA Wallet before funds go out. No
              one can send funds alone.
            </span>
          </>
        ),
        actionLabel: role === "owner" ? "Manage co-signing" : undefined,
        actionVariant: "tertiary",
      };

    case CO_SIGNING_VIEW.NOT_APPLICABLE:
      return { label: "", note: null, actionLabel: "" };
  }
};

export const toActiveSigner = (params: {
  userId: string;
  firstName: string;
  lastName: string;
  role: UserRole | null;
  stellarPublicKey?: string;
}): MultisigActiveSigner | null => {
  if (!isMultisigEligible(params.role)) return null;
  return {
    user_id: params.userId,
    first_name: params.firstName,
    last_name: params.lastName,
    role:
      params.role === "owner"
        ? MULTISIG_SIGNER_ROLE.OWNER
        : MULTISIG_SIGNER_ROLE.FINANCIAL_CONTROLLER,
    stellar_public_key: params.stellarPublicKey,
  };
};

export const truncatePublicKey = (key: string | undefined): string => {
  if (!key) return "";
  if (key.length <= 12) return key;
  return `${key.slice(0, 5)}...${key.slice(-5)}`;
};
